import { useEffect, useRef, useState } from 'react';

import { usePolling } from '@/hooks/usePolling';

import { ChatProgressWebSocket } from './ChatProgressWebSocket';
import type {
  ChainCompletedEvent,
  ChainFailedEvent,
  ChatProgressWebSocketStatus,
  StepProgressEvent,
  TitleUpdatedEvent,
} from './types';

const POLLING_INTERVAL_FAST_MS = 500;
const POLLING_INTERVAL_DEGRADED_MS = 30000;

interface UseChatProgressOptions {
  orgId: string | undefined;
  conversationId: string | null;
  taskId: string | null;
  onStepProgress: (event: StepProgressEvent) => void;
  onChainCompleted: (event: ChainCompletedEvent) => void;
  onChainFailed: (event: ChainFailedEvent) => void;
  onTitleUpdated: (event: TitleUpdatedEvent) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Polling data structure varies by task type
  onPollingUpdate: (data: any) => void;
  onPollingError: (error: string) => void;
  enabled?: boolean;
}

interface UseChatProgressReturn {
  wsStatus: ChatProgressWebSocketStatus;
  isPolling: boolean;
}

export function useChatProgress({
  orgId,
  conversationId,
  taskId,
  onStepProgress,
  onChainCompleted,
  onChainFailed,
  onTitleUpdated,
  onPollingUpdate,
  onPollingError,
  enabled = true,
}: UseChatProgressOptions): UseChatProgressReturn {
  const [wsStatus, setWsStatus] =
    useState<ChatProgressWebSocketStatus>('disconnected');
  const wsRef = useRef<ChatProgressWebSocket | null>(null);

  const onStepProgressRef = useRef(onStepProgress);
  const onChainCompletedRef = useRef(onChainCompleted);
  const onChainFailedRef = useRef(onChainFailed);
  const onTitleUpdatedRef = useRef(onTitleUpdated);

  useEffect(() => {
    onStepProgressRef.current = onStepProgress;
  });
  useEffect(() => {
    onChainCompletedRef.current = onChainCompleted;
  });
  useEffect(() => {
    onChainFailedRef.current = onChainFailed;
  });
  useEffect(() => {
    onTitleUpdatedRef.current = onTitleUpdated;
  });

  const pollingInterval =
    wsStatus === 'connected'
      ? POLLING_INTERVAL_DEGRADED_MS
      : POLLING_INTERVAL_FAST_MS;

  const { isPolling } = usePolling(taskId, conversationId, orgId, {
    enabled: !!enabled && !!taskId,
    interval: pollingInterval,
    onUpdate: onPollingUpdate,
    onError: onPollingError,
  });

  useEffect(() => {
    if (!enabled || !orgId || !conversationId) {
      if (wsRef.current) {
        wsRef.current.destroy();
        wsRef.current = null;
        setWsStatus('disconnected');
      }
      return;
    }

    const ws = new ChatProgressWebSocket({
      orgId,
      conversationId,
      onStepProgress: event => onStepProgressRef.current(event),
      onChainCompleted: event => onChainCompletedRef.current(event),
      onChainFailed: event => onChainFailedRef.current(event),
      onTitleUpdated: event => onTitleUpdatedRef.current(event),
      onStatusChange: setWsStatus,
    });
    wsRef.current = ws;
    ws.connect();

    return () => {
      ws.destroy();
      wsRef.current = null;
      setWsStatus('disconnected');
    };
  }, [enabled, orgId, conversationId]);

  return { wsStatus, isPolling };
}
